import { defineField, defineType } from 'sanity';

export default defineType({
  name: 'testimonial',
  title: 'Testimonial',
  type: 'document',
  fields: [
    defineField({
      name: 'customerName',
      title: 'Client Name',
      type: 'string',
      validation: Rule => Rule.required(),
    }),
    defineField({
      name: 'customerInitials',
      title: 'Short Name or Initials',
      type: 'string',
      description:
        'Shown instead of the full name when "Use Initials Only" is on, e.g. "Jennifer J." or "J.S."',
      validation: Rule => Rule.max(20),
    }),
    defineField({
      name: 'useInitialsOnly',
      title: 'Use Initials Only',
      type: 'boolean',
      description: 'Display initials instead of full name for privacy',
      initialValue: false,
    }),
    defineField({
      name: 'review',
      title: 'Review Text',
      type: 'text',
      description:
        'Full recommendations run long; keep excerpts under about 800 characters so cards stay readable.',
      validation: Rule => Rule.required().min(20).max(800),
    }),
    defineField({
      name: 'rating',
      title: 'Rating (1-5 stars)',
      type: 'number',
      description:
        'Optional. LinkedIn recommendations carry no rating; leave empty and no stars render.',
      validation: Rule => Rule.min(1).max(5),
    }),
    defineField({
      name: 'service',
      title: 'Service',
      type: 'reference',
      to: [{ type: 'service' }],
      description: 'The service this testimonial is about',
    }),
    defineField({
      name: 'caseStudy',
      title: 'Case Study',
      type: 'reference',
      to: [{ type: 'caseStudy' }],
      description: 'The engagement this testimonial came out of',
    }),
    defineField({
      name: 'role',
      title: 'Role & Company',
      type: 'string',
      description: 'e.g. "VP Engineering, Acme Logistics"',
    }),
    defineField({
      name: 'projectValue',
      title: 'Project Value Range',
      type: 'string',
      options: {
        list: [
          { title: 'Under $10k', value: 'under-10k' },
          { title: '$10k - $25k', value: '10k-25k' },
          { title: '$25k - $50k', value: '25k-50k' },
          { title: '$50k - $100k', value: '50k-100k' },
          { title: 'Over $100k', value: 'over-100k' },
          { title: 'Ongoing retainer', value: 'retainer' },
        ],
      },
    }),
    defineField({
      name: 'projectDate',
      title: 'Project Completion Date',
      type: 'date',
      validation: Rule => Rule.required(),
    }),
    defineField({
      name: 'source',
      title: 'Review Source',
      type: 'string',
      options: {
        list: [
          { title: 'Direct Client Feedback', value: 'direct' },
          { title: 'LinkedIn Recommendation', value: 'linkedin' },
          { title: 'Clutch', value: 'clutch' },
          { title: 'Google Reviews', value: 'google' },
          { title: 'Referral', value: 'referral' },
          { title: 'Email Follow-up', value: 'email' },
        ],
      },
      validation: Rule => Rule.required(),
    }),
    defineField({
      name: 'sourceUrl',
      title: 'Source URL',
      type: 'url',
      description: 'Link to original review (if applicable)',
    }),
    defineField({
      name: 'verified',
      title: 'Verified Review',
      type: 'boolean',
      description: 'Has this review been verified as authentic?',
      initialValue: false,
    }),
    defineField({
      name: 'featured',
      title: 'Featured Testimonial',
      type: 'boolean',
      description: 'Show in prominent testimonial sections',
      initialValue: false,
    }),
    defineField({
      name: 'showOnHomepage',
      title: 'Show on Homepage',
      type: 'boolean',
      description: 'Include in homepage testimonials carousel',
      initialValue: false,
    }),
    defineField({
      name: 'showOnServicePage',
      title: 'Show on Service Page',
      type: 'boolean',
      description: 'Display on related service page',
      initialValue: true,
    }),
    defineField({
      name: 'tags',
      title: 'Tags',
      type: 'array',
      of: [{ type: 'string' }],
      description:
        'Tags for categorization (delivery speed, communication, technical depth, etc.)',
      options: {
        layout: 'tags',
      },
    }),
    defineField({
      name: 'highlights',
      title: 'Key Highlights',
      type: 'array',
      of: [{ type: 'string' }],
      description:
        'Key aspects mentioned in review (responsiveness, code quality, ownership, etc.)',
      validation: Rule => Rule.max(5),
    }),
    defineField({
      name: 'customerPhoto',
      title: 'Client Photo',
      type: 'image',
      options: {
        hotspot: true,
      },
      description: 'Optional client photo (with permission)',
    }),
    defineField({
      name: 'permissionGranted',
      title: 'Publication Permission Granted',
      type: 'boolean',
      description: 'Client has given permission to use this review',
      validation: Rule => Rule.required(),
      initialValue: false,
    }),
    defineField({
      name: 'internalNotes',
      title: 'Internal Notes',
      type: 'text',
      description:
        'Private notes about this testimonial (not displayed publicly)',
    }),
    defineField({
      name: 'sortOrder',
      title: 'Sort Order',
      type: 'number',
      description: 'Order for displaying testimonials (lower numbers first)',
      initialValue: 100,
    }),
    defineField({
      name: 'published',
      title: 'Published',
      type: 'boolean',
      initialValue: true,
    }),
  ],
  orderings: [
    {
      title: 'Featured & Rating',
      name: 'featuredAndRating',
      by: [
        { field: 'featured', direction: 'desc' },
        { field: 'rating', direction: 'desc' },
        { field: 'projectDate', direction: 'desc' },
      ],
    },
    {
      title: 'Project Date (Newest)',
      name: 'projectDateDesc',
      by: [{ field: 'projectDate', direction: 'desc' }],
    },
    {
      title: 'Rating (Highest)',
      name: 'ratingDesc',
      by: [{ field: 'rating', direction: 'desc' }],
    },
    {
      title: 'Service & Date',
      name: 'serviceAndDate',
      by: [
        { field: 'service', direction: 'asc' },
        { field: 'projectDate', direction: 'desc' },
      ],
    },
  ],
  preview: {
    select: {
      customerName: 'customerName',
      customerInitials: 'customerInitials',
      useInitialsOnly: 'useInitialsOnly',
      review: 'review',
      rating: 'rating',
      service: 'service.title',
      caseStudy: 'caseStudy.title',
      featured: 'featured',
      showOnHomepage: 'showOnHomepage',
      verified: 'verified',
      published: 'published',
    },
    prepare(selection) {
      const {
        customerName,
        customerInitials,
        useInitialsOnly,
        review,
        rating,
        service,
        caseStudy,
        featured,
        showOnHomepage,
        verified,
        published,
      } = selection;

      const displayName =
        useInitialsOnly && customerInitials
          ? customerInitials
          : customerName || 'Anonymous';

      const badges = [];
      if (featured) badges.push('⭐');
      if (showOnHomepage) badges.push('🏠');
      if (verified) badges.push('✓');
      if (!published) badges.push('(Draft)');

      const badgeText = badges.length > 0 ? ` ${badges.join(' ')}` : '';
      const stars = '★'.repeat(rating || 0);
      const serviceText = service ? ` • ${service}` : '';
      const caseStudyText = caseStudy ? ` • ${caseStudy}` : '';

      return {
        title: `${stars} ${displayName}${badgeText}`,
        subtitle: `${review?.substring(0, 60)}...${serviceText}${caseStudyText}`,
      };
    },
  },
});
